package com.routexk.quiz.funnel

import com.routexk.quiz.utils.Storage
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

@Serializable
data class FunnelAnswers(
    val city: String? = null,
    val stopsIndex: Int? = null,
    val milesIndex: Int? = null,
    val hoursIndex: Int? = null,
    val daysIndex: Int? = null,
    val fuelIndex: Int? = null,
    val earningsIndex: Int? = null,
    val email: String? = null,
) {
    fun answerFor(id: QuizStepId): Int? = when (id) {
        QuizStepId.CITY -> null
        QuizStepId.STOPS -> stopsIndex
        QuizStepId.MILES -> milesIndex
        QuizStepId.HOURS -> hoursIndex
        QuizStepId.DAYS -> daysIndex
        QuizStepId.FUEL -> fuelIndex
        QuizStepId.EARNINGS -> earningsIndex
    }

    fun withAnswer(id: QuizStepId, index: Int): FunnelAnswers = when (id) {
        QuizStepId.CITY -> this
        QuizStepId.STOPS -> copy(stopsIndex = index)
        QuizStepId.MILES -> copy(milesIndex = index)
        QuizStepId.HOURS -> copy(hoursIndex = index)
        QuizStepId.DAYS -> copy(daysIndex = index)
        QuizStepId.FUEL -> copy(fuelIndex = index)
        QuizStepId.EARNINGS -> copy(earningsIndex = index)
    }

    val quizComplete: Boolean
        get() = city != null && stopsIndex != null && milesIndex != null &&
            hoursIndex != null && daysIndex != null && fuelIndex != null && earningsIndex != null

    /** Human-readable answers for session custom_data / analytics. */
    fun toCustomData(): Map<String, String> = buildMap {
        city?.let { put(QuizStepId.CITY.storageKey, it) }
        QuizStepId.entries.filter { it != QuizStepId.CITY }.forEach { id ->
            val index = answerFor(id) ?: return@forEach
            QuizDefinition.step(id).options?.getOrNull(index)?.let { put(id.storageKey, it) }
        }
    }
}

@Serializable
data class SelectedPlan(
    val uuid: String,
    val label: String,
    val amountCents: Long,
    val originalCents: Long,
    /** Length of the paid trial in days before the subscription renews. */
    val trialDays: Int = 0,
    /** Monthly amount the subscription renews at once the trial ends. */
    val renewalCents: Long = 0,
)

/**
 * Client-side funnel session persisted in localStorage so a refresh keeps
 * progress, the countdown deadline and the backend session binding.
 */
object FunnelStore {
    private const val ANSWERS_KEY = "funnel_answers"
    private const val SESSION_KEY = "funnel_session_uuid"
    private const val DEADLINE_KEY = "funnel_discount_deadline"
    private const val PLAN_KEY = "funnel_selected_plan"
    private const val INTENT_KEY = "funnel_intent_uuid"
    private const val PAID_KEY = "funnel_paid"
    private const val CITY_KEY = "funnel_detected_city"
    private const val PAYMENT_METHOD_KEY = "funnel_last_payment_method"
    private const val TRANSACTION_KEY = "funnel_last_transaction_id"

    private val json = Json { ignoreUnknownKeys = true }

    var answers: FunnelAnswers
        get() = Storage.get(ANSWERS_KEY)?.let {
            try {
                json.decodeFromString<FunnelAnswers>(it)
            } catch (_: Throwable) {
                FunnelAnswers()
            }
        } ?: FunnelAnswers()
        set(value) = Storage.save(ANSWERS_KEY, json.encodeToString(value))

    var sessionUuid: String?
        get() = Storage.get(SESSION_KEY)
        set(value) {
            if (value == null) Storage.remove(SESSION_KEY) else Storage.save(SESSION_KEY, value)
        }

    /** City resolved from the visitor's IP by the backend on session creation. */
    var detectedCity: String?
        get() = Storage.get(CITY_KEY)
        set(value) {
            if (value.isNullOrBlank()) Storage.remove(CITY_KEY) else Storage.save(CITY_KEY, value)
        }

    /** Discount deadline as epoch millis; created on first paywall visit. */
    var discountDeadline: Double?
        get() = Storage.get(DEADLINE_KEY)?.toDoubleOrNull()
        set(value) {
            if (value == null) Storage.remove(DEADLINE_KEY) else Storage.save(DEADLINE_KEY, value.toString())
        }

    var selectedPlan: SelectedPlan?
        get() = Storage.get(PLAN_KEY)?.let {
            try {
                json.decodeFromString<SelectedPlan>(it)
            } catch (_: Throwable) {
                null
            }
        }
        set(value) {
            if (value == null) Storage.remove(PLAN_KEY)
            else Storage.save(PLAN_KEY, json.encodeToString(value))
        }

    var intentUuid: String?
        get() = Storage.get(INTENT_KEY)
        set(value) {
            if (value == null) Storage.remove(INTENT_KEY) else Storage.save(INTENT_KEY, value)
        }

    var paid: Boolean
        get() = Storage.get(PAID_KEY) == "true"
        set(value) = Storage.save(PAID_KEY, value.toString())

    /**
     * Payment method chosen at checkout ('card' | 'apple_pay' | 'google_pay' |
     * ...), persisted for GA4 because wallet redirects reload the page.
     */
    var lastPaymentMethod: String?
        get() = Storage.get(PAYMENT_METHOD_KEY)
        set(value) {
            if (value == null) Storage.remove(PAYMENT_METHOD_KEY) else Storage.save(PAYMENT_METHOD_KEY, value)
        }

    /** Stripe PaymentIntent id of the completed payment (GA4 transaction_id). */
    var lastTransactionId: String?
        get() = Storage.get(TRANSACTION_KEY)
        set(value) {
            if (value == null) Storage.remove(TRANSACTION_KEY) else Storage.save(TRANSACTION_KEY, value)
        }

    val estimate: FunnelEstimate
        get() = EstimateCalculator.calculate(answers)

    /**
     * Drops everything tied to the previous funnel run — most importantly the
     * backend session binding: the old session maps to a Stripe customer that
     * may already hold an active subscription, which makes intent/create
     * reject the new checkout. [detectedCity] is kept (it's IP-based, not
     * session-based) so the city field still prefills instantly.
     */
    fun startNewRun() {
        sessionUuid = null
        answers = FunnelAnswers()
        selectedPlan = null
        intentUuid = null
        paid = false
        discountDeadline = null
        lastPaymentMethod = null
        lastTransactionId = null
    }
}
